import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { Box, Stack, Typography } from '@mui/material';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useQuery } from 'hooks/useQuery';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { ECustomFieldType } from 'models/customField';
import { EPermission } from 'models/permissions';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { findInDictionary } from 'helpers/dictionary';
import { mergeDictionaries } from '../Dictionaries';
import { EIconTypes } from 'constants/Icons';
import { EButtonSizesPX } from 'constants/Buttons';
import { ELegalStatus } from 'constants/LegalStatus';
import { ILegalEntityDeactivationWarnings } from 'models/legalEntity';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { LEModuleTypes } from 'constants/LegalEntityTypes';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

export interface IOrgLegalEntityData {
  city: string | null;
  organizationName: string;
  displayUniqueId: string;
  organisationTypeUniqueIds: string[];
  organisationCategoryUniqueId: string;
  departmentName: string;
  description: string;
  uniqueId: string;
  visibilityUniqueId: string;
  hasAssignedUser: boolean;
  primaryWeaponLicencingAuthorityUniqueId: string;
  secondaryWeaponLicencingAuthorityUniqueId: string;
  customFields?: ILegalEntityCustomField[];
  version: string;
  warnings: ILegalEntityDeactivationWarnings;

  [key: string]: any;
}

interface ILegalEntityDetailsProps {
  legalEntity: IOrgLegalEntityData;
  pageType?: EDetailsPageTypes;
  handleOpenDeactivate?: () => void;
  handleOpenActivate?: () => void;
  hasManagePermission?: boolean;
}

const OrgLegalEntityDetails = ({
  legalEntity,
  pageType = EDetailsPageTypes.view,
  handleOpenDeactivate,
  handleOpenActivate,
  hasManagePermission,
}: ILegalEntityDetailsProps) => {
  const navigate = useNavigate();
  const query = useQuery();
  const type: string = query.get('type')?.trim() || '';
  const { id = '' } = useParams<{ id: string }>();
  const {
    organisationStateType,
    organisationCivilianType,
    organisationPoliceType,
    organisationMilitaryType,
    loadDictionaries,
    legalVisibility,
    organisationCategory,
    weaponLicencingAuthority,
    legalStatus,
    legalOrganisationDeactivationReason,
  } = useLegalEntities();
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { canManageLE, canDeleteLE } = usePrivilegesValidation();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { getFieldsOptional, hiddenFieldsName } = useSystemFieldsConfiguration();

  useEffect(() => {
    getFieldsOptional([LEModuleTypes.organisation]);
  }, []);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.OrganisationCivilianType,
        EDictionaryTypes.OrganisationPoliceType,
        EDictionaryTypes.OrganisationMilitaryType,
        EDictionaryTypes.OrganisationStateType,
        EDictionaryTypes.LegalVisibility,
        EDictionaryTypes.OrganisationCategory,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.legalStatus,
        EDictionaryTypes.legalOrganisationDeactivationReason,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    getProperties();
  }, []);

  const renderCustomFieldData = (customField: ILegalEntityCustomField) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value;
  };

  const renderItemDetail = (dictionary: IDictionary[] | null, dictionaryItemId: string) => {
    const legal: IOrgLegalEntityData = legalEntity;
    if (legal) {
      return dictionary?.find(
        (item: IDictionary) =>
          item?.uniqueId === legal[dictionaryItemId as keyof IOrgLegalEntityData],
      )?.name;
    }
    return '';
  };

  const renderItemDetailList = (
    dictionary: IDictionary[] | null,
    dictionaryItemId: string,
  ) => {
    const legal: IOrgLegalEntityData = legalEntity;
    if (legal) {
      return legal[dictionaryItemId as keyof IOrgLegalEntityData]
        .map((uniqueId: string) => findInDictionary(uniqueId, dictionary))
        .join(', ');
    }
    return '';
  };

  return (
    <div>
      {legalEntity && (
        <DetailsTemplate
          deletePermissions={[EPermission.DELETE_LE]}
          editPermissions={[EPermission.MANAGE_LE]}
          title={legalEntity.organizationName}
          pageType={pageType}
          actionButtons={[
            ...(hasManagePermission &&
            legalEntity.statusUniqueId === ELegalStatus.INACTIVE &&
            handleOpenActivate
              ? [
                  {
                    icon: EIconTypes.checkmark,
                    sizePX: EButtonSizesPX.px32,
                    onClick: handleOpenActivate,
                  },
                ]
              : []),
            ...(hasManagePermission &&
            legalEntity.statusUniqueId === ELegalStatus.ACTIVE &&
            handleOpenDeactivate
              ? [
                  {
                    icon: EIconTypes.shutDown,
                    sizePX: EButtonSizesPX.px32,
                    onClick: handleOpenDeactivate,
                  },
                ]
              : []),
          ]}
          redirectToEditView={
            canManageLE(
              legalEntity.primaryWeaponLicencingAuthorityUniqueId,
              legalEntity.secondaryWeaponLicencingAuthorityUniqueId,
            )
              ? () =>
                  navigate(
                    `${ERouteLinks.EditLegalEntity.replace(':id', id)}?type=${type}&view=true`,
                  )
              : undefined
          }
          redirectToView={
            canDeleteLE(
              legalEntity.primaryWeaponLicencingAuthorityUniqueId,
              legalEntity.secondaryWeaponLicencingAuthorityUniqueId,
            )
              ? () =>
                  navigate(`${ERouteLinks.DeleteLegalEntity.replace(':id', id)}?type=${type}`)
              : undefined
          }
        >
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('deleteLegalEntity.legalEntityType.label')}
              </Typography>
              <Box>
                <Chips
                  id="legalEntityDetails-legalEntityType"
                  text={
                    type === 'individual'
                      ? t('legalEntityDetails.legalEntityType.individual.chip.label')
                      : t('legalEntityDetails.legalEntityType.organisation.chip.label')
                  }
                  variant={EChipsVariants.primary}
                />
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.displayUniqueId.label')}
              </Typography>
              <Typography variant="body2">{legalEntity.displayUniqueId}</Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.statusUniqueId.label')}
              </Typography>
              <Typography variant="body2">
                <Chips
                  id="statusUniqueId"
                  text={renderItemDetail(legalStatus, 'statusUniqueId')}
                  variant={
                    legalEntity.statusUniqueId === ELegalStatus.ACTIVE
                      ? EChipsVariants.success
                      : EChipsVariants.error
                  }
                />
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              {legalEntity.statusUniqueId === ELegalStatus.INACTIVE && (
                <>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.reasonOfDeactivation.label')}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(
                      renderItemDetail(
                        legalOrganisationDeactivationReason,
                        'deactivationReasonUniqueId',
                      ),
                    )}
                  </Typography>
                </>
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.name.label')}
              </Typography>
              <Typography id="legalEntityDetails-name" variant="body2">
                {legalEntity.organizationName}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.organisationCategory.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(
                  renderItemDetail(organisationCategory, 'organisationCategoryUniqueId'),
                )}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Grid>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('legalEntityDetails.organizationType.label')}
                </Typography>
              </Grid>
              <Typography id="legalEntityDetails-organizationType" variant="body2">
                {renderItemDetailList(
                  mergeDictionaries([
                    organisationStateType,
                    organisationCivilianType,
                    organisationMilitaryType,
                    organisationPoliceType,
                  ]),
                  'organisationTypeUniqueIds',
                )}
              </Typography>
            </Stack>
          </Grid>
          {!hiddenFieldsName.includes(EFieldsOptional.departmentName) && (
            <Grid size={{ xs: 12, lg: 3 }}>
              <Stack>
                <Grid>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.departmentName.label')}
                  </Typography>
                </Grid>
                <Typography
                  variant="body2"
                  id="legalEntityDetails-departmentName"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {emptyFieldHolder(legalEntity.departmentName)}
                </Typography>
              </Stack>
            </Grid>
          )}
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Grid>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('legalEntityDetails.description.label')}
                </Typography>
              </Grid>
              <Typography
                variant="body2"
                id="legalEntityDetails-description"
                sx={{ wordBreak: 'break-all' }}
              >
                {emptyFieldHolder(legalEntity.description)}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('deleteLegalEntity.visibility.label')}
              </Typography>
              <Box>
                <Chips
                  id="legalEntityDetails-visibility"
                  text={renderItemDetail(legalVisibility, 'visibilityUniqueId')}
                  variant={EChipsVariants.success}
                />
              </Box>
            </Stack>
          </Grid>
          {legalEntity.organisationCategoryUniqueId === EOrganisationCategory.civilian &&
            hasWeaponLicenceModule() && (
              <>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntityDetails.primaryWeaponLicencingAuthorityUniqueId.label')}
                    </Typography>
                    <Typography variant="body2">
                      {renderItemDetail(
                        weaponLicencingAuthority,
                        'primaryWeaponLicencingAuthorityUniqueId',
                      )}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntityDetails.secondaryWeaponLicencingAuthorityUniqueId.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(
                        renderItemDetail(
                          weaponLicencingAuthority,
                          'secondaryWeaponLicencingAuthorityUniqueId',
                        ),
                      )}
                    </Typography>
                  </Stack>
                </Grid>
              </>
            )}
          {legalEntity?.customFields?.map((customField) => (
            <Grid size={{ xs: 12, lg: 3 }}>
              <Stack
                key={customField.customFieldUniqueId}
                sx={{ gridColumn: { xs: 'span 6', lg: 'span 3' } }}
              >
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {customField?.translations?.filter(
                    (translation) =>
                      selectedLanguage?.uniqueId === translation.languageUniqueId,
                  )?.[0]?.name ||
                    customField?.translations?.filter(
                      (translation) =>
                        defaultLanguage?.uniqueId === translation.languageUniqueId,
                    )?.[0]?.name}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(renderCustomFieldData(customField))}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </DetailsTemplate>
      )}
    </div>
  );
};

export { OrgLegalEntityDetails };
